import { useAuthStore } from "@/store";
import { clearAuthSessionStorage, getValidAuthSessionFromStorage } from "@/utils/authSession";
import { useKycGateStore } from "@/store/useKycGateStore";
import axios from "axios";

// API configuration following the backend pattern.
//
// Which URL wins depends on Vite's env-file precedence
// (.env.[mode].local > .env.[mode] > .env.local > .env). In production mode
// this always resolves to the deployed backend unless VITE_API_URL is
// explicitly overridden. In dev mode, add a `.env.development.local` with
// VITE_API_URL=http://localhost:<PORT>/api to point at a local backend —
// see kolekto-fe-old/.env.development.local. The bare fallback below matches
// the backend's own default port. NOTE: app.js actually defaults to
// `PORT || 5050`, not 3000 — this comment previously claimed 3000, and the
// fallback below still says 3000. Left as-is deliberately rather than
// "corrected" to 5050: changing the dev fallback is a behaviour change for
// anyone already running a local proxy on 3000. Set VITE_API_BASE_URL in
// .env.development.local to point at your local backend (default
// http://localhost:5050/api) instead of relying on this fallback.
const API_BASE_URL =
  import.meta.env.MODE === "production"
    ? import.meta.env.VITE_API_URL || "https://api.kolekto.com.ng/api"
    : import.meta.env.VITE_API_BASE_URL ||
      import.meta.env.VITE_API_URL ||
      "http://localhost:3000/api";

// const { session } = useAuthStore()

export const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15_000, // 15 s — fail fast instead of hanging when backend is down
  withCredentials: true, // CRUCIAL: This sends cookies cross-domain
});

// Add a request interceptor to handle authentication headers
axiosInstance.interceptors.request.use(
  (config) => {
    const method = (config.method || "get").toLowerCase();
    const url = config.url || "";
    const isAmbassadorEndpoint = url.includes("/ambassadors/");
    const isAmbassadorPublicEndpoint = [
      "/ambassadors/apply",
      "/ambassadors/auth/signin",
      "/ambassadors/auth/setup-pin",
    ].some((endpoint) => url.includes(endpoint));
    const isAuthPublicEndpoint = [
      "/auth/signin",
      "/auth/signup",
      "/auth/magic-link",
      "/auth/resend-verification",
      "/auth/forgot-password",
      "/auth/reset-password",
    ].some((endpoint) => url.includes(endpoint));

    // Only set JSON content-type when we are actually sending a body.
    // This avoids unnecessary preflight noise for GET/HEAD requests.
    if (
      !["get", "head", "options"].includes(method) &&
      !(config.data instanceof FormData)
    ) {
      config.headers["Content-Type"] = "application/json";
    }

    if (isAmbassadorEndpoint && !isAmbassadorPublicEndpoint) {
      const ambassadorToken = localStorage.getItem("kolekto-ambassador-token");
      if (ambassadorToken) {
        config.headers.Authorization = `Bearer ${ambassadorToken}`;
      } else {
        delete config.headers.Authorization;
      }
      return config;
    }

    // Get session from localStorage
    const session = !isAuthPublicEndpoint ? getValidAuthSessionFromStorage() : null;
    if (session?.access_token) {
      // Add Authorization header with Bearer token
      config.headers.Authorization = `Bearer ${session.access_token}`;
    } else {
      delete config.headers.Authorization;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Ambassador public endpoints that don't require authentication.
// 401s from these are normal credential failures, not session expiry.
const AMBASSADOR_PUBLIC_ENDPOINTS = [
  "/ambassadors/apply",
  "/ambassadors/auth/signin",
  "/ambassadors/auth/setup-pin",
];

function isAmbassadorPublicUrl(url: string | undefined): boolean {
  if (!url) return false;
  return AMBASSADOR_PUBLIC_ENDPOINTS.some((endpoint) => url.includes(endpoint));
}

// Public auth endpoints — a 401 from these is a normal credential failure
// (e.g. wrong password on /auth/signin) and must NOT trigger a hard logout.
const AUTH_PUBLIC_ENDPOINTS = [
  "/auth/signin",
  "/auth/signup",
  "/auth/magic-link",
  "/auth/resend-verification",
  "/auth/forgot-password",
  "/auth/reset-password",
];

function isPublicAuthCall(url: string | undefined): boolean {
  if (!url) return false;
  return AUTH_PUBLIC_ENDPOINTS.some((endpoint) => url.includes(endpoint));
}

// Only the canonical session-check endpoint is allowed to trigger an auto-
// logout. Other endpoints' 401s are treated as transient — typically a
// concurrent-request race against the BE's cookie-based token refresh:
//
//   1. User submits a withdrawal. Bearer token is stale.
//   2. verifyToken refreshes via the refresh_token cookie → succeeds →
//      Set-Cookie returns the new access_token. Withdrawal completes.
//   3. The FE immediately fires GET /withdrawals/eligible-collections,
//      GET /dashboard/activities, GET /dashboard/collections/:id/stats
//      in parallel — but the OLD Bearer header is still attached because
//      localStorage hasn't been touched, and the refresh_token cookie
//      may have been rotated and not yet flushed to all in-flight requests.
//   4. One of those requests 401s.
//   5. Previously: the 401 handler kicked off signOut() → user yanked to
//      /login mid-page, losing the post-withdrawal UI update.
//
// Now we only auto-logout when /auth/me itself says the session is dead.
// Other 401s fail their individual callers (they can show a retry toast),
// but the user stays logged in and a single successful subsequent request
// will keep the session alive.
function shouldAutoLogout(url: string | undefined): boolean {
  if (!url) return false;
  if (url.includes("/ambassadors/")) return false;
  return url.includes("/auth/me");
}

// Coalesce concurrent 401s on /auth/me — without this, two near-simultaneous
// session checks would each call signOut() and race the navigation.
let signingOutPromise: Promise<void> | null = null;

// Track and rate-limit 401s on non-/auth/me endpoints. If we see a *sustained*
// stream (>=5 401s within 10s) we conclude the session really is dead and
// trigger the logout, rather than a transient refresh race.
// NOTE: ambassador endpoint 401s are handled separately and excluded here.
const recent401s: number[] = [];
const STALE_SESSION_THRESHOLD = 5;
const STALE_SESSION_WINDOW_MS = 10_000;

function looksLikeDeadSession(): boolean {
  const now = Date.now();
  recent401s.push(now);
  // Drop entries older than the window.
  while (recent401s.length && now - recent401s[0] > STALE_SESSION_WINDOW_MS) {
    recent401s.shift();
  }
  return recent401s.length >= STALE_SESSION_THRESHOLD;
}

// Ambassador endpoints use a separate JWT system. When a 401 is received for
// an ambassador protected route, we clear the ambassador session and redirect
// to the ambassador login — without touching the main organizer session.
const AMBASSADOR_TOKEN_KEY = "kolekto-ambassador-token";
const AMBASSADOR_PROFILE_KEY = "kolekto-ambassador-profile";
// Read once by the ambassador login page on mount to explain a forced
// logout (e.g. "Your ambassador account has been temporarily suspended...").
// A plain login failure never touches this — only a session that was valid
// and got kicked out mid-use sets it.
export const AMBASSADOR_LOGOUT_NOTICE_KEY = "kolekto-ambassador-logout-notice";
let ambassadorRedirectPending = false;
function handleAmbassadorUnauthorized(message?: string) {
  if (ambassadorRedirectPending) return;
  ambassadorRedirectPending = true;
  localStorage.removeItem(AMBASSADOR_TOKEN_KEY);
  localStorage.removeItem(AMBASSADOR_PROFILE_KEY);
  if (message) {
    sessionStorage.setItem(AMBASSADOR_LOGOUT_NOTICE_KEY, message);
  }
  // Defer to allow the current request's error to propagate first.
  setTimeout(() => {
    ambassadorRedirectPending = false;
    const current = window.location.pathname;
    if (!current.startsWith("/ambassador/login")) {
      window.history.replaceState(null, "", "/ambassador/login");
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
  }, 0);
}

async function performSignOutAndRedirect() {
  if (!signingOutPromise) {
    signingOutPromise = (async () => {
      try {
        await useAuthStore.getState().signOut();
      } catch (err) {
        console.error("[axios] signOut on 401 failed:", err);
        clearAuthSessionStorage();
        useAuthStore.setState({ user: null, session: null } as any);
      } finally {
        const path = window.location.pathname;
        const onPublicPage = ["/login", "/register", "/auth/verify", "/forgot-password", "/reset-password", "/"].includes(path)
          || path.startsWith("/contribute/")
          || path.startsWith("/payment/");
        if (!onPublicPage) {
          window.history.replaceState(null, "", "/login");
          window.dispatchEvent(new PopStateEvent("popstate"));
        }
        signingOutPromise = null;
      }
    })();
  }
  await signingOutPromise;
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const url = error.config?.url;

    // Ambassador endpoints use a dedicated JWT + live status check
    // (verifyAmbassador re-validates ambassador_profiles.status on every
    // request). A 403 there means the token is still cryptographically
    // valid but the account is no longer 'accepted' (suspended/rejected) —
    // that must force an immediate logout exactly like a 401 does, so this
    // is checked independently of, and before, the organizer/admin 401
    // handling below (which must never react to ambassador 403s).
    if (
      (status === 401 || status === 403) &&
      url?.includes("/ambassadors/") &&
      !isAmbassadorPublicUrl(url)
    ) {
      handleAmbassadorUnauthorized(error.response?.data?.error);
      return Promise.reject(error);
    }

    // Backend-first KYC enforcement (legacy-user migration): ANY endpoint
    // that blocks an action for lack of identity verification returns
    // { code: "KYC_REQUIRED" } alongside its 403. Catching it here — once —
    // means every current and future gated endpoint gets the same
    // "Identity verification required" modal instead of a generic error,
    // without each call site needing its own special-case handling.
    // `message` is the field the backend contract specifies; `error` is the
    // backwards-compatible alias every older endpoint still sends. Reading
    // both means the modal shows real copy no matter which path answered
    // (Express errorHandler, an inline controller response, or an edge
    // function) — and `feature` lets the modal say WHICH action was blocked.
    if (error.response?.data?.code === "KYC_REQUIRED") {
      const data = error.response.data;
      useKycGateStore.getState().open(data.message || data.error, data.feature);
      return Promise.reject(error);
    }

    if (status === 401 && !isPublicAuthCall(url)) {
      if (shouldAutoLogout(url)) {
        // Canonical session check failed → session is definitively dead.
        await performSignOutAndRedirect();
      } else if (looksLikeDeadSession()) {
        // Sustained 401s across multiple endpoints → not just a refresh
        // race; really logged out. Trigger the auto-logout.
        console.warn("[axios] sustained 401s — signing out");
        await performSignOutAndRedirect();
      }
      // Otherwise: let the individual call fail. The user stays logged in.
      // The next successful request (or the cookie refresh on the next
      // verifyToken) will resync the session naturally.
    }
    return Promise.reject(error);
  }
);
